
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { inventoryData } from '../data/inventory';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td, Button } from '../components/UI';
import { 
  Search, Download, Check, AlertCircle, AlertTriangle, 
  ArrowUpRight, LayoutList, Grid3x3, ShoppingCart, Plus, Minus, Package, Clock, Info, ChevronLeft, ChevronRight
} from '../components/Icons';
import { StockStatus, InventoryItem } from '../types';
import { useTranslation } from '../contexts/LanguageContext';

const SupplierProductList: React.FC = () => {
  const { user } = useAuth();
  const { addItem, totalItems } = useCart();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'GRID' | 'LIST'>('LIST');
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [viewingProduct, setViewingProduct] = useState<InventoryItem | null>(null);

  const tabs = ['All', ...Object.values(StockStatus)];

  const handleColumnFilterChange = (key: string, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value }));
  };

  const myProducts = useMemo(() => {
    if (!user || !user.companyName) return [];
    
    let products = inventoryData.filter(item => item.supplier === user.companyName);
    
    // Status Filter
    if (statusFilter !== 'All') {
      products = products.filter(item => item.status === statusFilter);
    }

    // Search Filter
    if (search) {
      const s = search.toLowerCase();
      products = products.filter(item => 
        item.itemNumber.toLowerCase().includes(s) || 
        item.itemName.toLowerCase().includes(s)
      );
    }

    // Column Filters
    products = products.filter(item => {
      return Object.entries(columnFilters).every(([key, value]) => {
        if (!value) return true;
        const itemValue = String((item as any)[key] || '').toLowerCase();
        return itemValue.includes((value as string).toLowerCase());
      });
    });

    return products;
  }, [user, search, statusFilter, columnFilters]);

  // ... (Keep existing helpers: getStatusBadge, getMockDetails, updateLocalQty, handleAddToCart)
  const getStatusBadge = (status: StockStatus) => {
    switch (status) {
      case StockStatus.HEALTHY:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-900/30"><Check size={10} strokeWidth={4} /> {t('healthy')}</span>;
      case StockStatus.CRITICAL:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-900/30"><AlertCircle size={10} strokeWidth={4} /> {t('critical')}</span>;
      case StockStatus.WARNING:
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-black uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/30"><AlertTriangle size={10} strokeWidth={4} /> {t('warning')}</span>;
      default: return null;
    }
  };

  const getMockDetails = (itemId: string) => {
    const base = itemId.charCodeAt(0);
    return {
      price: base / 2,
      leadTime: Math.floor(base % 5) + 2 + ' days'
    };
  };

  const updateLocalQty = (id: string, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [id]: Math.max(1, (prev[id] || 1) + delta)
    }));
  };

  const handleAddToCart = (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    const price = getMockDetails(item.id).price;
    const qty = quantities[item.id] || 1;
    addItem(item, qty, price);
    setQuantities(prev => ({ ...prev, [item.id]: 1 }));
  };

  // ... (Modal rendering logic omitted for brevity, keeping same)
  if (viewingProduct) {
    const details = getMockDetails(viewingProduct.id);
    return (
      <div className="p-6 space-y-6 max-w-[1400px] mx-auto animate-in slide-in-from-right duration-500">
        <PageHeader title={viewingProduct.itemName} breadcrumbs={[{ label: 'Product Catalog' }, { label: viewingProduct.itemNumber }]} onBack={() => setViewingProduct(null)} actions={<Button onClick={(e) => handleAddToCart(e, viewingProduct)} className="px-8 shadow-lg"><Plus size={16} className="mr-2" /> ADD TO CART</Button>} />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
           <Card className="p-10 space-y-8 flex flex-col items-center border-t-4 border-t-brand-medium"><div className="w-40 h-40 bg-gray-50 dark:bg-[#1A1A1A] rounded-sm flex items-center justify-center border border-gray-100 dark:border-[#333] shadow-inner mb-2"><Package size={80} className="text-gray-200" /></div><div className="text-center space-y-2"><h2 className="text-2xl font-bold text-brand-dark dark:text-white uppercase tracking-tight">{viewingProduct.itemName}</h2><p className="text-xs font-mono text-gray-400 tracking-[0.2em]">{viewingProduct.itemNumber}</p><div className="pt-4">{getStatusBadge(viewingProduct.status)}</div></div><div className="w-full pt-8 border-t border-gray-100 dark:border-[#333] space-y-5"><div className="flex justify-between items-center text-sm"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Base Price</span><span className="font-black text-brand-dark dark:text-white text-lg">CHF {details.price.toFixed(2)}</span></div><div className="flex justify-between items-center text-sm"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Avg. Lead Time</span><span className="font-bold dark:text-white">{details.leadTime}</span></div><div className="flex justify-between items-center text-sm"><span className="text-gray-400 font-bold uppercase text-[10px] tracking-widest">Customer ID</span><span className="font-mono text-xs dark:text-white">CUST-88421</span></div></div></Card>
           <div className="lg:col-span-2 space-y-8"><Card className="p-8"><div className="flex items-center gap-3 mb-6"><Info size={18} className="text-brand-medium" /><h3 className="text-xs font-black uppercase tracking-widest text-brand-dark dark:text-white">Consignment Logic</h3></div><div className="grid grid-cols-2 md:grid-cols-4 gap-8"><div className="space-y-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Min. Stock</p><p className="text-xl font-black text-brand-dark dark:text-white">{viewingProduct.minStock}</p></div><div className="space-y-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Current Stock</p><p className={`text-xl font-black ${viewingProduct.currentStock < viewingProduct.minStock ? 'text-red-600' : 'text-brand-dark dark:text-white'}`}>{viewingProduct.currentStock}</p></div><div className="space-y-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Lot Size</p><p className="text-xl font-black text-gray-500">25 Units</p></div><div className="space-y-1"><p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Safety Days</p><p className="text-xl font-black text-gray-500">14 Days</p></div></div></Card><Card className="p-10 bg-brand-dark text-white border-none shadow-xl flex items-center justify-between"><div className="space-y-2"><p className="text-[10px] font-black uppercase tracking-widest text-brand-bright">Quick Order</p><h4 className="text-xl font-bold tracking-tight italic">Direct fulfillment to Main Hub</h4></div><div className="flex items-center gap-4"><div className="flex items-center gap-4 bg-white/10 p-2 rounded-sm border border-white/10"><button onClick={() => updateLocalQty(viewingProduct.id, -1)} className="p-2 hover:text-brand-bright transition-colors"><Minus size={14} /></button><span className="text-lg font-black w-8 text-center">{quantities[viewingProduct.id] || 1}</span><button onClick={() => updateLocalQty(viewingProduct.id, 1)} className="p-2 hover:text-brand-bright transition-colors"><Plus size={14} /></button></div><Button onClick={(e) => handleAddToCart(e, viewingProduct)} className="h-14 px-10 !bg-brand-bright !text-brand-dark shadow-xl hover:brightness-110">ORDER NOW</Button></div></Card></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-[1920px] mx-auto animate-in fade-in duration-500 relative">
      <PageHeader 
        title={t('my_products')} 
        breadcrumbs={[{ label: user?.companyName || 'Supplier' }, { label: t('my_products') }]}
        actions={
          <div className="flex items-center gap-4">
             <div className="flex bg-gray-100 dark:bg-[#1A1A1A] p-1 rounded-sm">
                <button onClick={() => setViewMode('LIST')} className={`p-2 transition-all ${viewMode === 'LIST' ? 'bg-white dark:bg-[#262626] shadow-sm text-brand-dark dark:text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`} title={t('list_view')}><LayoutList size={16} /></button>
                <button onClick={() => setViewMode('GRID')} className={`p-2 transition-all ${viewMode === 'GRID' ? 'bg-white dark:bg-[#262626] shadow-sm text-brand-dark dark:text-white' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`} title={t('grid_view')}><Grid3x3 size={16} /></button>
             </div>
             <button onClick={() => navigate('/supplier-cart')} className="relative h-10 px-6 bg-brand-dark text-white rounded-sm font-bold text-[10px] uppercase tracking-widest hover:bg-brand-medium transition-all shadow-md flex items-center gap-2">
                <ShoppingCart size={16} /> {t('cart')}
                {totalItems > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] border-2 border-white dark:border-black">{totalItems}</span>}
             </button>
          </div>
        }
      />

      <Card className="overflow-hidden border-t-4 border-t-brand-medium">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-[#393939] bg-white dark:bg-[#161616] flex flex-wrap items-center justify-between gap-4">
           {/* Tab Switch */}
           <div className="flex items-center gap-1 bg-[#F5F7FA] dark:bg-[#262626] p-1 rounded-md">
            {tabs.map(tab => (
              <button
                key={tab}
                onClick={() => setStatusFilter(tab)}
                className={`h-8 px-4 text-[10px] font-bold uppercase tracking-widest transition-all rounded-sm ${
                  statusFilter === tab
                    ? 'bg-white dark:bg-[#161616] text-brand-dark dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-brand-dark dark:hover:text-white'
                }`}
              >
                {tab === 'All' ? t('all') : tab}
              </button>
            ))}
          </div>

           <div className="flex items-center gap-3">
              <div className="relative group">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-medium transition-colors" />
                <input type="text" placeholder={t('search_resources')} value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 pl-9 pr-4 bg-[#F5F7FA] dark:bg-[#262626] border border-transparent focus:border-brand-medium focus:bg-white dark:focus:bg-[#1F1F1F] rounded-sm text-xs w-64 outline-none transition-all" />
              </div>
              <button className="h-9 px-4 flex items-center gap-2 bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#393939] hover:bg-gray-50 dark:hover:bg-[#333] text-gray-600 dark:text-gray-300 text-[10px] font-bold uppercase tracking-widest rounded-sm transition-all"><Download size={14} /> {t('export_list')}</button>
           </div>
        </div>

        {viewMode === 'LIST' ? (
          <Table>
            <Thead>
              <Tr isHeader>
                <Th>
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_number')}</span>
                    <input 
                      className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                      placeholder={t('filter_placeholder')}
                      onChange={(e) => handleColumnFilterChange('itemNumber', e.target.value)}
                    />
                  </div>
                </Th>
                <Th>
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('item_name')}</span>
                    <input 
                      className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400"
                      placeholder={t('filter_placeholder')}
                      onChange={(e) => handleColumnFilterChange('itemName', e.target.value)}
                    />
                  </div>
                </Th>
                <Th className="text-right">
                  <div className="flex flex-col gap-2 items-end">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('unit_price')}</span>
                    <input 
                      className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                      placeholder={t('filter_placeholder')}
                      onChange={(e) => handleColumnFilterChange('unitPrice', e.target.value)}
                    />
                  </div>
                </Th>
                <Th className="text-right">
                  <div className="flex flex-col gap-2 items-end">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('current_stock')}</span>
                    <input 
                      className="w-20 bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-2 focus:outline-none focus:border-brand-medium transition-colors text-[#161616] dark:text-white placeholder-gray-400 text-right"
                      placeholder={t('filter_placeholder')}
                      onChange={(e) => handleColumnFilterChange('currentStock', e.target.value)}
                    />
                  </div>
                </Th>
                <Th>
                  <div className="flex flex-col gap-2">
                    <span className="text-[10px] font-bold uppercase text-gray-500 dark:text-gray-400 leading-none">{t('status')}</span>
                    <select 
                      className="w-full bg-white dark:bg-[#1F1F1F] border border-gray-200 dark:border-gray-700 rounded-sm h-8 text-xs px-1 focus:outline-none focus:border-brand-medium transition-colors cursor-pointer text-gray-600 dark:text-gray-300"
                      onChange={(e) => handleColumnFilterChange('status', e.target.value)}
                    >
                      <option value="">{t('all')}</option>
                      <option value="Healthy">{t('healthy')}</option>
                      <option value="Critical">{t('critical')}</option>
                      <option value="Warning">{t('warning')}</option>
                    </select>
                  </div>
                </Th>
                <Th className="text-right">
                   {t('quantity')}
                </Th>
                <Th className="text-right">
                   {t('actions')}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {myProducts.length > 0 ? myProducts.map((item) => (
                <Tr key={item.id} onClick={() => setViewingProduct(item)}>
                  <Td><span className="font-bold text-brand-dark dark:text-white font-mono text-xs">{item.itemNumber}</span></Td>
                  <Td className="text-sm font-medium text-gray-700 dark:text-gray-300">{item.itemName}</Td>
                  <Td className="text-right text-sm text-gray-600 dark:text-gray-400">CHF {getMockDetails(item.id).price.toFixed(2)}</Td>
                  <Td className="text-right"><span className={`text-sm font-bold ${item.currentStock < item.minStock ? 'text-red-600' : 'text-gray-700 dark:text-gray-300'}`}>{item.currentStock.toLocaleString()}</span></Td>
                  <Td>{getStatusBadge(item.status)}</Td>
                  <Td className="text-right"><div className="inline-flex items-center gap-2 bg-gray-50 dark:bg-[#262626] p-1 rounded border border-gray-200 dark:border-[#333]" onClick={(e) => e.stopPropagation()}><button onClick={() => updateLocalQty(item.id, -1)} className="p-1 hover:text-brand-dark dark:hover:text-white"><Minus size={10} /></button><span className="text-xs font-bold w-6 text-center">{quantities[item.id] || 1}</span><button onClick={() => updateLocalQty(item.id, 1)} className="p-1 hover:text-brand-dark dark:hover:text-white"><Plus size={10} /></button></div></Td>
                  <Td className="text-right"><Button size="sm" onClick={(e) => handleAddToCart(e, item)}><Plus size={14} className="mr-1" /> {t('add_to_cart')}</Button></Td>
                </Tr>
              )) : (
                <Tr><Td colSpan={7} className="text-center py-12 text-gray-400 font-bold uppercase tracking-widest">No products found matching filters.</Td></Tr>
              )}
            </Tbody>
          </Table>
        ) : (
          <div className="p-6 bg-gray-50 dark:bg-[#111111] grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {myProducts.map(item => (
              <Card key={item.id} onClick={() => setViewingProduct(item)} className="p-0 overflow-hidden flex flex-col hover:border-brand-medium group transition-all cursor-pointer">
                 <div className="aspect-[4/3] bg-gray-50 dark:bg-[#1A1A1A] flex items-center justify-center border-b border-gray-100 dark:border-[#333] relative"><Package size={64} className="text-gray-200 dark:text-gray-700 group-hover:text-brand-light transition-colors" /><div className="absolute top-4 left-4">{getStatusBadge(item.status)}</div></div>
                 <div className="p-6 flex-1 flex flex-col"><div className="mb-4"><p className="text-[10px] font-mono text-gray-400 uppercase tracking-widest mb-1">{item.itemNumber}</p><h4 className="text-sm font-bold text-brand-dark dark:text-white h-10 line-clamp-2">{item.itemName}</h4></div>
                 <div className="mt-auto pt-4 border-t border-gray-100 dark:border-[#333] flex items-center justify-between"><span className="text-lg font-black text-brand-dark dark:text-white">CHF {getMockDetails(item.id).price.toFixed(2)}</span><div className="flex items-center gap-1"><span className="text-[9px] font-bold text-gray-400 uppercase">{t('current_stock')}:</span><span className={`text-xs font-bold ${item.currentStock < item.minStock ? 'text-red-600' : 'text-gray-600 dark:text-gray-400'}`}>{item.currentStock}</span></div></div>
                 <div className="mt-6 flex items-center gap-3" onClick={(e) => e.stopPropagation()}><div className="flex-1 flex items-center justify-between bg-gray-100 dark:bg-[#262626] px-3 py-2 rounded-sm border border-gray-200 dark:border-[#333]"><button onClick={() => updateLocalQty(item.id, -1)}><Minus size={14} /></button><span className="text-sm font-bold">{quantities[item.id] || 1}</span><button onClick={() => updateLocalQty(item.id, 1)}><Plus size={14} /></button></div><Button className="flex-[1.5]" onClick={(e) => handleAddToCart(e, item)}>{t('add_to_cart')}</Button></div></div>
              </Card>
            ))}
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 dark:border-[#393939] flex items-center justify-between gap-2 bg-gray-50/50 dark:bg-[#161616]">
          <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
            Showing {myProducts.length} entries
          </div>
          <div className="flex items-center gap-1">
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronLeft size={16} /></button>
            <button className="p-1.5 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SupplierProductList;
